import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@mui/material";
import { useEffect, useLayoutEffect, useMemo } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";

import { CheckCircle as CheckCircleIcon, RemoveCircle as RemoveCircleIcon } from "@mui/icons-material";
import { addDays, differenceInDays, format } from "date-fns";
import { useBudgetFormContext, verifyRuleApplies } from "../../../context/BudgetFormContext";
import { getDayOfWeek, stringToDate } from "../../../mapping/taxMapping";
import { createDatesArr } from "../../../utils/utils";

const HeadColumn = ({ date }) => {
  const parsedDate = stringToDate(date);
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <span
        style={{
          fontWeight: 700,
          fontSize: 13,
          color: "#343A40",
          lineHeight: 1,
        }}
      >
        {getDayOfWeek(parsedDate)}
      </span>
      <span
        style={{
          fontWeight: 500,
          fontSize: 12,
          color: "#ADB5BD",
        }}
      >
        {format(parsedDate, "dd/MM")}
      </span>
    </Box>
  );
};

const FormList = () => {
  const _ = require("lodash");
  const {
    data,
    params,
    selectedDates,
    setSelectedDates,
    setCheckedRules,
    checkedRules,
    changeParams,
    activeCategory,
    stayLength,
    lowestPossibleCategory
  } = useBudgetFormContext();

  useEffect(() => {
    setCheckedRules((prev) => {
      const next = { ...prev };
      let changed = false;

      (data.rules || []).forEach((rule) => {
        if (next[rule.rule_description]?.checked && !verifyRuleApplies(selectedDates, rule)) {
          next[rule.rule_description] = {
            ...next[rule.rule_description],
            checked: false,
            order: null,
          };
          changed = true;
        }
      });

      if (!changed) return prev;

      let currentOrder = 1;
      Object.keys(next).forEach((rule) => {
        if (next[rule]?.checked) {
          next[rule] = { ...next[rule], order: currentOrder };
          currentOrder += 1;
        }
      });

      return next;
    });
  }, [selectedDates, data.rules, setCheckedRules]);

  const displayPrices = useMemo(() => {
    if (!activeCategory) return data.pricesByCategoria[lowestPossibleCategory];

    return data.pricesByCategoria[activeCategory] || [];
  }, [activeCategory, data.pricesByCategoria, data.prices]);

  const getColor = (date, item) => {

    switch (item?.restriction_type) {
        case 1:
          return "#d9d9d9"; //"gray"; stop
        case 2:
          return "#e0c3fc"; //"purple"; fecho
        case 3:
          return  "#c7f9cc";  //"green"; fecho_in
        case 4:
          return "#f7a9a8"; //"pink"; fecho_out
        default:
          if (selectedDates.includes(date)) return "#FFEC99";

          const lastSelectedDate = selectedDates[selectedDates.length - 1];

          if (!lastSelectedDate) return "#fff";

          const parsedLastDate = stringToDate(lastSelectedDate);

          if (!parsedLastDate) return "#fff";

          const checkoutDate = format(
            addDays(parsedLastDate, 1),
            "dd/MM/yyyy"
          );

          if (checkoutDate === date) return "linear-gradient(to right, #ffec99 50%, transparent 50%)";
          
          return "#fff";
    }
  };

	const baseColors = [        
		'#F89F0F', 
		'#268BE1',
		'#a0bff7f0',  
		'#082B49',
		'#034EA2',
		'#6B2DD8CC'
	];

// const [dateColors, setDateColors] = useState([...baseColors]);

// const getColorTematica = (id_tematica) => {
// 	if (!id_tematica) return '#172b4d'

// 	if (dateColors.length === 0) {
// 		setDateColors([...baseColors])
// 		return baseColors[id_tematica % baseColors.length]
// 	}

// 	const pickedColor = dateColors[id_tematica % dateColors.length]

// 	setDateColors(prev => prev.filter(color => color !== pickedColor))

// 	return pickedColor
// }

const getColorTematica = (id_tematica) => {
    if (!id_tematica) return '#172b4d'

    const pickedColor = baseColors[id_tematica % baseColors.length]

    return pickedColor
}

  useLayoutEffect(() => {
    const container = document.querySelector("#scrollbar");
    if (!container) return;
    
    const diffDays = differenceInDays(params.check_out, params.check_in);

    container.scrollLeft = 480 + diffDays * 30;
  }, [data.dates]);

  const displayDates = useMemo(() => {
    if (!data.dates || data.dates.length === 0) return [];
    return data.dates;
  }, [data.dates]);

  const handleClickColumn = (displayIndex) => {
    const clickedDateStr = displayDates[displayIndex];

    if (!clickedDateStr) return;

    let newCheckIn = stringToDate(clickedDateStr);

    if (!newCheckIn) return;

    let newCheckOut = addDays(newCheckIn, stayLength);

    const lastAvailableDateStr =
      displayDates.at(-2) || displayDates.at(-1);

    if (!lastAvailableDateStr) return;

    const lastAvailableDate =
      stringToDate(lastAvailableDateStr);

    if (!lastAvailableDate) return;

    if (newCheckOut > addDays(lastAvailableDate, 1)) {
      newCheckOut = addDays(lastAvailableDate, 1);

      newCheckIn = addDays(newCheckOut, -stayLength);
    }

    changeParams({
      ...params,
      check_in: newCheckIn,
      check_out: newCheckOut,
    });

    setSelectedDates(createDatesArr(newCheckIn, newCheckOut));
  };
  if (Object.keys(data.tax).length === 0) return null;

  return (
    <Box sx={{ display: "flex" }}>
      <Box sx={{ }}>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell height={52.5}>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#343A40",
                    lineHeight: 1,
                  }}
                >
                  Tarifário
                </span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.tax.map(({ acommodation_type }) => (
              <TableRow key={acommodation_type}>
                <TableCell padding={"none"} sx={{ padding: "6px 0 ", whiteSpace: "nowrap"  }}>
                  <p style={{paddingRight: '3px'}}>{acommodation_type}</p>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableHead>
            <TableRow>
              <TableCell height={49.5}>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#343A40",
                    lineHeight: 1,
                  }}
                >
                  Valores (R$)
                </span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayPrices.map(({ acommodation_description }) => (
              <TableRow key={acommodation_description}>
                <TableCell padding={"none"} sx={{ padding: "6px 0", fontSize: 11, height: '49.5px', whiteSpace: "nowrap" }}>
                  {acommodation_description}
                  <br />
                  <br />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>

          <TableHead>
            <TableRow>
              <TableCell height={49.5}>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#343A40",
                    lineHeight: 1,
                  }}
                >
                  Regras
                </span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {data.rules.map((rule) => {
            const { rule_description, flag_cumulative, id_tematica } = rule;
            const ruleApplies = verifyRuleApplies(selectedDates, rule);

            return rule_description !== "." ? (
              <TableRow key={rule_description}>
                <TableCell
                  height={52.5}
                  padding={"none"}
                  style={{color: getColorTematica(`${id_tematica}`.slice(0, -4)), opacity: ruleApplies ? 1 : 0.5}}
                  sx={{ minWidth: 220, padding: "6px 0", display: 'flex', alignItems: 'center', fontSize: '12px' }}
                >
                  {rule_description} &nbsp;
                  <Checkbox
                    sx={{ padding: 0 }}
                    disabled={!ruleApplies}
                    checked={checkedRules[rule_description]?.checked || false}
                    onChange={(_) => {
                      const checks = { ...checkedRules };
                      const checkOrderCounter = Object.values(checks).filter(rule => rule.checked === true).length;

                      if (flag_cumulative === "N") {
                        Object.keys(checks).forEach((rule) => {
                          if (checks[rule].flag_cumulative === "N" && rule !== rule_description) {
                            checks[rule] = { ...checks[rule], checked: false, order: null }; 
                          }
                        });
                      }

                      const isChecked = !checks[rule_description]?.checked;

                      if (isChecked) {
                        checks[rule_description] = {
                          checked: true,
                          flag_cumulative,
                          order: checkOrderCounter + 1
                        };
                      } else {
                        checks[rule_description] = {
                          ...checks[rule_description],
                          checked: false,
                          order: null
                        };

                        let currentOrder = 1;
                        Object.keys(checks).forEach((rule) => {
                          if (checks[rule].checked) {
                            checks[rule] = { ...checks[rule], order: currentOrder };
                            currentOrder += 1;
                          }
                        });
                      }
                      setCheckedRules(checks);
                    }}
                    value="true"
                  />

                </TableCell>
              </TableRow>
            ) : null;
          })}  
          </TableBody>
          <TableHead>
            <TableRow>
              <TableCell height={49.5} >
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#343A40",
                    lineHeight: 1,
                  }}
                >
                  Temáticas
                </span>
              </TableCell>
            </TableRow>
          	</TableHead>
			<TableBody>
				{data.tematicas.map((item) => (
					<TableRow key={item.id_tematica}>
						<TableCell
						height={52.5}
						padding={"none"}
						sx={{ minWidth: 220, padding: "6px 0" }}
						>
							<span style={{ color: getColorTematica(item.id_tematica) }}>
								{item.ds_tematica}
							</span>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
        </Table>
      </Box>
      <PerfectScrollbar id={"scrollbar"} style={{paddingBottom: '8px'}}>
        <Box sx={{ width: "100%", pr: 4 }}>
          <Table size={"small"}>
            <TableHead>
              <TableRow>
                {displayDates.map((date, i) => (
                  <TableCell
                    align={"center"}
                    key={date}
                    sx={{
                      background: getColor(date),
                      minWidth: 64,
                      cursor: "pointer",
                      height: '52.5px'
                    }}
                    onClick={(_) => handleClickColumn(i)}
                  >
                    <HeadColumn date={date} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.tax.map((item) => (
                <TableRow key={item.acommodation_type}>
                  {displayDates.map((date) => (
                    <TableCell
                      padding={"none"}
                      align={"center"}
                      key={date}
                      sx={{ background: getColor(date), padding: "6px 0", fontWeight: item[date] === undefined || item[date] === null ? 400 : item[date].includes("BF") ? 700 : 400 }}
                    >
                      {item[date]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
            <TableHead>
              <TableRow>
                {displayDates.map((date) => (
                  <TableCell
                    height={49.5}
                    align={"center"}
                    key={date}
                    sx={{ background: getColor(date), minWidth: 64 }}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayPrices.map((item) => (
                <TableRow key={item.acommodation_description}>
                  {displayDates.map((date) => (
                    <TableCell
                      padding={"none"}
                      align={"center"}
					            height={49.5}
                      key={date}
                      sx={{
                        background: getColor(date, item[date]),
                        verticalAlign: "text-top",
                        fontWeight: item[date]?.tax_ds === undefined || item[date]?.tax_ds === null
                          ? 400 
                          : item[date]?.tax_ds.includes('BF') || item[date]?.daily_price === 0 
                            ? 700 
                            : 400
                      }}
                    >
                      {item[date]?.daily_price !== undefined && item[date]?.daily_price !== null ? item[date]?.daily_price : 0}
                      <br />
                      <span style={{ fontSize: "11px" }}>
                        {item[date]?.min_room_nights || ""}
                      </span>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>

            <TableHead>
              <TableRow>
                {displayDates.map((date) => (
                  <TableCell
                    height={49.5}
                    align={"center"}
                    key={date}
                    sx={{ background: getColor(date), minWidth: 64 }}
                  />
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {data.rules.map((item) =>
                item.rule_description !== "." ? (
                  <TableRow key={item.rule_description}>
                    {displayDates.map((date) => {
                      return (
                        <TableCell
                          padding={"none"}
                          align={"center"}
                          key={date}
                          sx={{
                            background: getColor(date, item[date]),
                            height: "52.5px",
                          }}
                        >
                          {item[date]?.rule_id ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                              }}
                            >
                              <CheckCircleIcon
                                sx={{
                                  height: 34,
                                  fill: item.id_tematica
                                    ? getColorTematica(
                                        `${item.id_tematica}`.slice(0, -4)
                                      )
                                    : "green",
                                }}
                              />

                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "green",
                                }}
                              >
                                {item?.perc_discount
                                  ? `${item.perc_discount}% OFF`
                                  : "-"
                                }
                              </span>
                            </div>
                          ) : (
                            ""
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ) : null
              )}
            </TableBody>
            <TableHead>
              <TableRow>
                {displayDates.map((date) => (
                  <TableCell
                    height={49.5}
                    align={"center"}
                    key={date}
                    sx={{ background: getColor(date), minWidth: 64 }}
                  />
                ))}
              </TableRow>
            </TableHead>
			<TableBody>
			{data.tematicas.map((tematica) => (
				<TableRow key={`tematica-${tematica.id_tematica || tematica.ds_tematica}`}>
				{displayDates.map((dateKey) => {
					const info = tematica.dates[dateKey];

					const hasRule = info?.rule_id;
					const isValid = info?.rule_description !== ".";

          const isOutOfRange = tematica.dt_inicio < dateKey && tematica.dt_fim > dateKey;

					if (!hasRule && (!isValid || isOutOfRange)) {
            return (
              <TableCell 
                height={52.5}
                key={`${tematica.id_tematica}-${dateKey}`} 
                align="center" 
                sx={{
                  background: getColor(dateKey, info),
                  padding: 0,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                  <RemoveCircleIcon sx={{ height: 34, fill: getColorTematica(tematica.id_tematica) }} />
                  <span style={{ fontSize: "11px", color: "#082B49" }}>-</span>
                </div>
              </TableCell>
            );
					}

					return (
					<TableCell
						key={`${tematica.id_tematica}-${dateKey}`}
						padding="none"
						align="center"
						sx={{
						background: getColor(dateKey, info),
              padding: "0",
              height: 52.5
						}}
					>
						{hasRule ? (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", padding: 0 }}>
							<CheckCircleIcon sx={{ height: 34, fill: getColorTematica(tematica.id_tematica) }} />
							<span style={{ fontSize: "11px", color: "green", padding: 0 }}>
                {info?.perc_discount
                  ? `${info.perc_discount}% OFF`
                  : "-"
                }
							</span>
						</div>
						) : (
						""
						)}
					</TableCell>
					);
				})}
				</TableRow>
			))}
			</TableBody>
          </Table>
        </Box>
      </PerfectScrollbar>
    </Box>
  );
};

export default FormList;
